import { inject, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { Translation } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DeepRequired } from 'shared/framework/util/typescript';
import { ThemeEnum } from '../../enums/theme/theme.enum';
import { ThemeService } from '../../services/theme/theme.service';
import { DEFAULT_THEME } from './default-theme.token';

export const provideTheming = (theme?: ThemeEnum) => {
    const nonceElement = document.querySelector('[ngCspNonce]');
    const nonce = nonceElement?.getAttribute('ngCspNonce');
    return [
        {
            provide: DEFAULT_THEME,
            useValue: theme,
        },
        provideAppInitializer(() => {
            inject(ThemeService).init();
        }),
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        provideAnimationsAsync('noop'),
        providePrimeNG({
            csp: {
                nonce: nonce ?? undefined,
            },
            theme: {
                preset: definePreset(Aura, {
                    semantic: {
                        primary: {
                            50: '{blue.50}',
                            100: '{blue.100}',
                            200: '{blue.200}',
                            300: '{blue.300}',
                            400: '{blue.400}',
                            500: '{blue.500}',
                            600: '{blue.600}',
                            700: '{blue.700}',
                            800: '{blue.800}',
                            900: '{blue.900}',
                            950: '{blue.950}',
                        },
                    },
                    components: {
                        button: {
                            root: {
                                borderRadius: '2rem',
                                paddingX: '1rem',
                                paddingY: '0.5rem',
                            },
                        },
                    },
                }),
                options: {
                    darkModeSelector: false,
                },
            },
            translation: {
                firstDayOfWeek: 1,
                startsWith: $localize`:@@primeng.startsWith:Starts with`,
                contains: $localize`:@@primeng.contains:Contains`,
                notContains: $localize`:@@primeng.notContains:Not contains`,
                endsWith: $localize`:@@primeng.endsWith:Ends with`,
                equals: $localize`:@@primeng.equals:Equals`,
                completed: $localize`:@@primeng.completed:Completed`,
                notEquals: $localize`:@@primeng.notEquals:Not equals`,
                noFilter: $localize`:@@primeng.noFilter:No Filter`,
                lt: $localize`:@@primeng.lt:Less than`,
                lte: $localize`:@@primeng.lte:Less than or equal to`,
                gt: $localize`:@@primeng.gt:Greater than`,
                gte: $localize`:@@primeng.gte:Greater than or equal to`,
                is: $localize`:@@primeng.is:Is`,
                isNot: $localize`:@@primeng.isNot:Is not`,
                before: $localize`:@@primeng.before:Before`,
                after: $localize`:@@primeng.after:After`,
                dateIs: $localize`:@@primeng.dateIs:Date is`,
                dateIsNot: $localize`:@@primeng.dateIsNot:Date is not`,
                dateBefore: $localize`:@@primeng.dateBefore:Date is before`,
                dateAfter: $localize`:@@primeng.dateAfter:Date is after`,
                clear: $localize`:@@primeng.clear:Clear`,
                apply: $localize`:@@primeng.apply:Apply`,
                matchAll: $localize`:@@primeng.matchAll:Match all`,
                matchAny: $localize`:@@primeng.matchAny:Match Any`,
                addRule: $localize`:@@primeng.addRule:Add Rule`,
                removeRule: $localize`:@@primeng.removeRule:Remove Rule`,
                accept: $localize`:@@glossary.yes:Yes`,
                reject: $localize`:@@glossary.no:No`,
                choose: $localize`:@@primeng.choose:Choose`,
                upload: $localize`:@@primeng.upload:Upload`,
                cancel: $localize`:@@primeng.cancel:Cancel`,
                dayNames: [
                    $localize`:@@primeng.day.sunday:Sunday`,
                    $localize`:@@primeng.day.monday:Monday`,
                    $localize`:@@primeng.day.tuesday:tuesday`,
                    $localize`:@@primeng.day.wednesday:Wednesday`,
                    $localize`:@@primeng.day.thursday:Thursday`,
                    $localize`:@@primeng.day.friday:Friday`,
                    $localize`:@@primeng.day.saturday:Saturday`,
                ],
                dayNamesShort: [
                    $localize`:@@primeng.day.short.sunday:Sun`,
                    $localize`:@@primeng.day.short.monday:Mon`,
                    $localize`:@@primeng.day.short.tuesday:Tue`,
                    $localize`:@@primeng.day.short.wednesday:Wed`,
                    $localize`:@@primeng.day.short.thursday:Thu`,
                    $localize`:@@primeng.day.short.friday:Fri`,
                    $localize`:@@primeng.day.short.saturday:Sat`,
                ],
                dayNamesMin: [
                    $localize`:@@primeng.day.shortest.sunday:Su`,
                    $localize`:@@primeng.day.shortest.monday:Mo`,
                    $localize`:@@primeng.day.shortest.tuesday:Tu`,
                    $localize`:@@primeng.day.shortest.wednesday:We`,
                    $localize`:@@primeng.day.shortest.thursday:Th`,
                    $localize`:@@primeng.day.shortest.friday:Fr`,
                    $localize`:@@primeng.day.shortest.saturday:Sa`,
                ],
                monthNames: [
                    $localize`:@@primeng.month.january:January`,
                    $localize`:@@primeng.month.february:February`,
                    $localize`:@@primeng.month.march:March`,
                    $localize`:@@primeng.month.april:April`,
                    $localize`:@@glossary.month.may:May`,
                    $localize`:@@primeng.month.june:June`,
                    $localize`:@@primeng.month.july:July`,
                    $localize`:@@primeng.month.august:August`,
                    $localize`:@@primeng.month.september:September`,
                    $localize`:@@primeng.month.october:October`,
                    $localize`:@@primeng.month.november:November`,
                    $localize`:@@primeng.month.december:December`,
                ],
                monthNamesShort: [
                    $localize`:@@primeng.month.short.january:Jan`,
                    $localize`:@@primeng.month.short.february:Feb`,
                    $localize`:@@primeng.month.short.march:Mar`,
                    $localize`:@@primeng.month.short.april:Apr`,
                    $localize`:@@glossary.month.may:May`,
                    $localize`:@@primeng.month.short.june:Jun`,
                    $localize`:@@primeng.month.short.july:Jul`,
                    $localize`:@@primeng.month.short.august:Aug`,
                    $localize`:@@primeng.month.short.september:Sep`,
                    $localize`:@@primeng.month.short.october:Oct`,
                    $localize`:@@primeng.month.short.november:Nov`,
                    $localize`:@@primeng.month.short.december:Dec`,
                ],
                dateFormat: $localize`:@@primeng.dateFormat:mm/dd/yy`,
                today: $localize`:@@primeng.today:Today`,
                weekHeader: $localize`:@@primeng.weekHeader:Wk`,
                weak: $localize`:@@primeng.weak:Weak`,
                medium: $localize`:@@primeng.medium:Medium`,
                strong: $localize`:@@primeng.strong:Strong`,
                passwordPrompt: $localize`:@@primeng.passwordPrompt:Enter a password`,
                emptyMessage: $localize`:@@glossary.noResults:No results found`,
                emptyFilterMessage: $localize`:@@glossary.noResults:No results found`,
                aria: {
                    trueLabel: $localize`:@@primeng.aria.trueLabel:True`,
                    falseLabel: $localize`:@@primeng.aria.falseLabel:False`,
                    nullLabel: $localize`:@@primeng.aria.nullLabel:Not Selected`,
                    star: $localize`:@@primeng.aria.star:1 star`,
                    stars: $localize`:@@primeng.aria.stars:{star} stars`,
                    selectAll: $localize`:@@primeng.aria.selectAll:All items selected`,
                    unselectAll: $localize`:@@primeng.aria.unselectAll:All items unselected`,
                    close: $localize`:@@primeng.aria.close:Close`,
                    previous: $localize`:@@glossary.previous:Previous`,
                    next: $localize`:@@glossary.next:Next`,
                    navigation: $localize`:@@primeng.aria.navigation:Navigation`,
                    scrollTop: $localize`:@@primeng.aria.scrollTop:Scroll Top`,
                    moveTop: $localize`:@@glossary.moveUp:Move Up`,
                    moveUp: $localize`:@@glossary.moveUp:Move Up`,
                    moveDown: $localize`:@@primeng.aria.moveDown:Move Down`,
                    moveBottom: $localize`:@@primeng.aria.moveBottom:Move Bottom`,
                    moveToTarget: $localize`:@@primeng.aria.moveToTarget:Move to Target`,
                    moveToSource: $localize`:@@primeng.aria.moveToSource:Move to Source`,
                    moveAllToTarget: $localize`:@@primeng.aria.moveAllToTarget:Move All to Target`,
                    moveAllToSource: $localize`:@@primeng.aria.moveAllToSource:Move All to Source`,
                    pageLabel: $localize`:@@primeng.aria.pageLabel:Page {page}`,
                    firstPageLabel: $localize`:@@primeng.aria.firstPageLabel:First Page`,
                    lastPageLabel: $localize`:@@primeng.aria.lastPageLabel:Last Page`,
                    nextPageLabel: $localize`:@@primeng.aria.nextPageLabel:Next Page`,
                    prevPageLabel: $localize`:@@primeng.aria.previousPageLabel:Previous Page`,
                    listLabel: $localize`:@@primeng.aria.listLabel:List`,
                    browseFiles: $localize`:@@primeng.aria.browserFiles:Browse`,
                    maximizeLabel: $localize`:@@primeng.aria.maximizeLabel:Maximize`,
                    minimizeLabel: $localize`:@@primeng.aria.minimizeLabel:Minimize`,
                    removeLabel: $localize`:@@glossary.delete:Delete`,
                    selectColor: $localize`:@@primeng.aria.selectColor:Select color`,
                    previousPageLabel: $localize`:@@primeng.aria.previousPageLabel:Previous Page`,
                    rowsPerPageLabel: $localize`:@@primeng.aria.rowsPerPageLabel:Rows per page`,
                    jumpToPageDropdownLabel: $localize`:@@primeng.aria.jumpToPageDropdownLabel:Jump to Page Dropdown`,
                    jumpToPageInputLabel: $localize`:@@primeng.aria.jumpToPageInputLabel:Jump to Page Input`,
                    selectRow: $localize`:@@primeng.aria.selectRow:Row Selected`,
                    unselectRow: $localize`:@@primeng.aria.unselectRow:Row Unselected`,
                    expandRow: $localize`:@@primeng.aria.expandRow:Row Expanded`,
                    collapseRow: $localize`:@@primeng.aria.collapseRow:Row Collapsed`,
                    showFilterMenu: $localize`:@@primeng.aria.showFilterMenu:Show Filter Menu`,
                    hideFilterMenu: $localize`:@@primeng.aria.hideFilterMenu:Hide Filter Menu`,
                    filterOperator: $localize`:@@primeng.aria.filterOperator:Filter Operator`,
                    filterConstraint: $localize`:@@primeng.aria.filterConstraint:Filter Constraint`,
                    editRow: $localize`:@@primeng.aria.editRow:Edit Row`,
                    saveEdit: $localize`:@@primeng.aria.saveEdit:Save Edit`,
                    cancelEdit: $localize`:@@primeng.aria.cancelEdit:Cancel Edit`,
                    listView: $localize`:@@primeng.aria.listView:List View`,
                    gridView: $localize`:@@primeng.aria.gridView:Grid View`,
                    slide: $localize`:@@primeng.aria.slide:Slide`,
                    slideNumber: $localize`:@@primeng.aria.slideNumber:{slideNumber}`,
                    zoomImage: $localize`:@@primeng.aria.zoomImage:Zoom Image`,
                    zoomIn: $localize`:@@primeng.aria.zoomIn:Zoom In`,
                    zoomOut: $localize`:@@primeng.aria.zoomOut:Zoom Out`,
                    rotateRight: $localize`:@@primeng.aria.rotateRight:Rotate Right`,
                    rotateLeft: $localize`:@@primeng.aria.rotateLeft:Rotate Left`,
                },
                am: $localize`:@@primeng.am:am`,
                chooseYear: $localize`:@@primeng.chooseYear:Choose Year`,
                chooseMonth: $localize`:@@primeng.chooseMonth:Choose Month`,
                chooseDate: $localize`:@@primeng.chooseDate:Choose Date`,
                pm: $localize`:@@primeng.pm:pm`,
                prevDecade: $localize`:@@primeng.prevDecade:Previous Decade`,
                nextDecade: $localize`:@@primeng.nextDecade:Next Decade`,
                prevYear: $localize`:@@primeng.prevYear:Previous Year`,
                nextYear: $localize`:@@primeng.nextYear:Next Year`,
                prevMonth: $localize`:@@primeng.prevMonth:Previous Month`,
                nextMonth: $localize`:@@primeng.nextMonth:Next Month`,
                prevHour: $localize`:@@primeng.prevHour:Previous Hour`,
                nextHour: $localize`:@@primeng.nextHour:Next Hour`,
                prevMinute: $localize`:@@primeng.prevMinute:Previous Minute`,
                nextMinute: $localize`:@@primeng.nextMinute:Next Minute`,
                prevSecond: $localize`:@@primeng.prevSecond:Previous Second`,
                nextSecond: $localize`:@@primeng.nextSecond:Next Second`,
                pending: $localize`:@@glossary.pending:Pending`,
                emptySelectionMessage: $localize`:@@primeng.emptySelectionMessage:No selected item`,
                emptySearchMessage: $localize`:@@glossary.noResults:No results found`,
                selectionMessage: $localize`:@@primeng.selectionMessage:{0} items selected`,
                searchMessage: $localize`:@@primeng.searchMessage:{0} results are available`,
                fileSizeTypes: [
                    $localize`:@@primeng.fileSizeType.B:B`,
                    $localize`:@@primeng.fileSizeType.KB:KB`,
                    $localize`:@@primeng.fileSizeType.MB:MB`,
                    $localize`:@@primeng.fileSizeType.GB:GB`,
                    $localize`:@@primeng.fileSizeType.TB:TB`,
                    $localize`:@@primeng.fileSizeType.PB:PB`,
                    $localize`:@@primeng.fileSizeType.EB:EB`,
                    $localize`:@@primeng.fileSizeType.ZB:ZB`,
                    $localize`:@@primeng.fileSizeType.YB:YB`,
                ],
                noFileChosenMessage: $localize`:@@primeng.noFileChosenMessage:No file chosen`,
                fileChosenMessage: $localize`:@@primeng.fileChosenMessage:File chosen`,
            } satisfies DeepRequired<Translation>,
        }),
    ];
};
